<template>
  <div class="json-tree-viewer">
    <!-- Object -->
    <template v-if="isObject">
      <div v-if="!innerOnly" class="json-line" :style="{ paddingLeft: indentPx + 'px' }">
        <span class="json-bracket">{</span>
      </div>
      <template v-for="key in objectKeys">
        <div
          v-if="!isExpandable(data[key])"
          :key="key"
          class="json-line"
          :style="{ paddingLeft: (indentPx + indentStep) + 'px' }"
        >
          <span class="json-key">"{{ key }}"</span><span class="json-colon">: </span>
          <span :class="valueClass(data[key])">{{ formatPrimitive(data[key]) }}</span><span class="json-comma">{{ isLast(key) ? '' : ',' }}</span>
        </div>
        <template v-else>
          <div
            :key="key"
            class="json-line json-line--expandable"
            :style="{ paddingLeft: (indentPx + indentStep) + 'px' }"
            @click="toggle(key)"
          >
            <span class="json-toggle">
              <v-icon x-small>{{ expandedKeys.has(key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            </span>
            <span class="json-key">"{{ key }}"</span><span class="json-colon">: </span>
            <span v-if="!expandedKeys.has(key)" class="json-bracket">{{ Array.isArray(data[key]) ? '[' : '{' }} {{ summary(data[key]) }}{{ Array.isArray(data[key]) ? ']' : '}' }}</span><span v-else class="json-bracket">{{ Array.isArray(data[key]) ? '[' : '{' }}</span><span class="json-comma">{{ isLast(key) ? '' : ',' }}</span>
          </div>
          <div v-if="expandedKeys.has(key)" class="json-nested">
            <JsonTreeViewer
              :data="data[key]"
              :depth="depth + 1"
              :expand-all="expandAll"
              :inner-only="true"
            />
            <div class="json-line" :style="{ paddingLeft: (indentPx + indentStep) + 'px' }">
              <span class="json-bracket">{{ Array.isArray(data[key]) ? ']' : '}' }}</span><span class="json-comma">{{ isLast(key) ? '' : ',' }}</span>
            </div>
          </div>
        </template>
      </template>
      <div v-if="!innerOnly" class="json-line" :style="{ paddingLeft: indentPx + 'px' }">
        <span class="json-bracket">}</span>
      </div>
    </template>

    <!-- Array -->
    <template v-else-if="isArray">
      <div v-if="!innerOnly" class="json-line" :style="{ paddingLeft: indentPx + 'px' }">
        <span class="json-bracket">[</span>
      </div>
      <template v-for="(value, index) in data">
        <div
          v-if="!isExpandable(value)"
          :key="index"
          class="json-line"
          :style="{ paddingLeft: (indentPx + indentStep) + 'px' }"
        >
          <span :class="valueClass(value)">{{ formatPrimitive(value) }}</span><span class="json-comma">{{ index < data.length - 1 ? ',' : '' }}</span>
        </div>
        <template v-else>
          <div
            :key="index"
            class="json-line json-line--expandable"
            :style="{ paddingLeft: (indentPx + indentStep) + 'px' }"
            @click="toggle(String(index))"
          >
            <span class="json-toggle">
              <v-icon x-small>{{ expandedKeys.has(String(index)) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            </span>
            <span v-if="!expandedKeys.has(String(index))" class="json-bracket">{{ Array.isArray(value) ? '[' : '{' }} {{ summary(value) }}{{ Array.isArray(value) ? ']' : '}' }}</span><span v-else class="json-bracket">{{ Array.isArray(value) ? '[' : '{' }}</span><span class="json-comma">{{ index < data.length - 1 ? ',' : '' }}</span>
          </div>
          <div v-if="expandedKeys.has(String(index))" class="json-nested">
            <JsonTreeViewer
              :data="value"
              :depth="depth + 1"
              :expand-all="expandAll"
              :inner-only="true"
            />
            <div class="json-line" :style="{ paddingLeft: (indentPx + indentStep) + 'px' }">
              <span class="json-bracket">{{ Array.isArray(value) ? ']' : '}' }}</span><span class="json-comma">{{ index < data.length - 1 ? ',' : '' }}</span>
            </div>
          </div>
        </template>
      </template>
      <div v-if="!innerOnly" class="json-line" :style="{ paddingLeft: indentPx + 'px' }">
        <span class="json-bracket">]</span>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'JsonTreeViewer',

  props: {
    data: {
      type: [Object, Array],
      default: () => ({}),
    },
    depth: {
      type: Number,
      default: 0,
    },
    expandAll: {
      type: Boolean,
      default: false,
    },
    /** When true, do not render outer { } or [ ]; used for expanded nested blocks. */
    innerOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expandedKeys: new Set(),
    };
  },

  computed: {
    isObject() {
      return this.data !== null && !Array.isArray(this.data) && typeof this.data === 'object';
    },

    isArray() {
      return Array.isArray(this.data);
    },

    objectEntries() {
      return this.isObject ? this.data : {};
    },

    objectKeys() {
      return this.isObject ? Object.keys(this.data) : [];
    },

    indentPx() {
      return 8 + this.depth * 20;
    },

    indentStep: () => 20,
  },

  watch: {
    expandAll: {
      immediate: true,
      handler(v) {
        if (!v) return;
        if (this.isObject) {
          this.objectKeys.forEach((k) => {
            if (this.isExpandable(this.data[k])) this.expandedKeys.add(k);
          });
        }
        if (this.isArray) {
          this.data.forEach((val, i) => {
            if (this.isExpandable(val)) this.expandedKeys.add(String(i));
          });
        }
        this.expandedKeys = new Set(this.expandedKeys);
      },
    },
  },

  methods: {
    isExpandable(val) {
      return val !== null && typeof val === 'object';
    },

    isLast(key) {
      const keys = this.objectKeys;
      return keys.indexOf(key) === keys.length - 1;
    },

    toggle(key) {
      if (this.expandedKeys.has(key)) {
        this.expandedKeys.delete(key);
      } else {
        this.expandedKeys.add(key);
      }
      this.expandedKeys = new Set(this.expandedKeys);
    },

    summary(val) {
      if (Array.isArray(val)) return val.length + ' items';
      return Object.keys(val).length + ' keys';
    },

    formatPrimitive(val) {
      if (val === null) return 'null';
      if (typeof val === 'string') return JSON.stringify(val);
      if (typeof val === 'number' || typeof val === 'boolean') return String(val);
      return String(val);
    },

    valueClass(val) {
      if (val === null) return 'json-value json-value--null';
      if (typeof val === 'string') return 'json-value json-value--string';
      if (typeof val === 'number') return 'json-value json-value--number';
      if (typeof val === 'boolean') return 'json-value json-value--boolean';
      return 'json-value';
    },
  },
};
</script>

<style scoped>
.json-tree-viewer {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  user-select: text;
}

.json-line {
  min-height: 20px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 4px;
}

.json-line--expandable {
  cursor: pointer;
}

.json-line--expandable:hover {
  background: rgba(255, 255, 255, 0.06);
}

.json-toggle {
  display: inline-flex;
  width: 16px;
  flex-shrink: 0;
  color: #808080;
}

.json-key {
  color: #9cdcfe;
}

.json-colon {
  color: #808080;
  margin-left: 2px;
}

.json-bracket {
  color: #808080;
}

.json-comma {
  color: #808080;
}

.json-value--string {
  color: #ce9178;
}

.json-value--number {
  color: #b5cea8;
}

.json-value--boolean {
  color: #569cd6;
}

.json-value--null {
  color: #569cd6;
}

.json-nested {
  margin-left: 0;
}
</style>
